<template>
  <div id="contact-page">
    <Navigation />
    
    <!-- Hero 联系区 -->
    <section class="contact-hero">
      <div class="container">
        <div class="hero-wrapper">
          <div class="hero-content">
            <div class="header-label">{{ $t('contact.label') }}</div>
            <h1 class="hero-title">{{ $t('contact.title') }}</h1>
            <p class="hero-subtitle">{{ $t('contact.subtitle') }}</p>
          </div>
          
          <div class="hero-card">
            <div class="card-header">{{ $t('contact.officeLocation') }}</div>
            <div class="card-location">
              <p class="location-name">{{ $t('contact.locationName') }}</p>
              <p class="location-address">{{ $t('contact.locationAddress') }}</p>
            </div>
            <div class="card-divider"></div>
            <div class="card-meta">
              <span class="meta-item">
                <i class="icon-marker"></i>
                {{ $t('contact.locationRegion') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系信息区 -->
    <section class="contact-info">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ $t('contact.multiWays') }}</h2>
          <p class="section-subtitle">{{ $t('contact.selectWays') }}</p>
        </div>
        
        <div class="info-cards-grid">
          <!-- 公司地址 -->
          <div class="info-card">
            <div class="card-accent-line"></div>
            <div class="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 8 13 8 13s8-7.75 8-13c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
              </svg>
            </div>
            <h3 class="card-title">{{ $t('contact.address') }}</h3>
            <p class="card-text">{{ $t('contact.locationAddress') }}</p>
            <div class="card-footer">{{ $t('contact.locationName') }}</div>
          </div>

          <!-- 商务邮箱 -->
          <div class="info-card">
            <div class="card-accent-line"></div>
            <div class="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M2 6l10 7 10-7"/>
              </svg>
            </div>
            <h3 class="card-title">{{ $t('contact.email') }}</h3>
            <p class="card-text">{{ $t('contact.businessEmail') }}</p>
            <div class="card-footer">{{ $t('contact.businessEmail') }}</div>
          </div>

          <!-- 联系电话 -->
          <div class="info-card">
            <div class="card-accent-line"></div>
            <div class="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </div>
            <h3 class="card-title">{{ $t('contact.phone') }}</h3>
            <p class="card-text">{{ $t('contact.businessPhone') }}</p>
            <div class="card-footer">{{ $t('contact.businessPhone') }}</div>
          </div>

          <!-- 合作方式 -->
          <div class="info-card">
            <div class="card-accent-line"></div>
            <div class="card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <h3 class="card-title">{{ $t('contact.cooperation') }}</h3>
            <p class="card-text">{{ $t('contact.cooperationDesc') }}</p>
            <div class="card-footer">{{ $t('contact.cooperation') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系表单区 -->
    <section id="contact-form" class="contact-form-section">
      <div class="container">
        <div class="form-wrapper">
          <div class="form-header">
            <h2 class="form-title">{{ $t('contact.contactForm') }}</h2>
            <p class="form-subtitle">{{ $t('contact.formSubtitle') }}</p>
          </div>

          <form class="contact-form" @submit.prevent="handleSubmit">
            <div class="form-row">
              <div class="form-group">
                <label for="name" class="form-label">{{ $t('contact.name') }}</label>
                <input
                  type="text"
                  id="name"
                  v-model="formData.name"
                  class="form-input"
                  :placeholder="$t('contact.name')"
                  required
                />
              </div>
              <div class="form-group">
                <label for="email" class="form-label">{{ $t('nav.contact') }}</label>
                <input
                  type="email"
                  id="email"
                  v-model="formData.email"
                  class="form-input"
                  :placeholder="$t('contact.emailPlaceholder')"
                  required
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="company" class="form-label">{{ $t('contact.company') }}</label>
                <input
                  type="text"
                  id="company"
                  v-model="formData.company"
                  class="form-input"
                  :placeholder="$t('contact.companyPlaceholder')"
                  required
                />
              </div>
              <div class="form-group">
                <label for="purpose" class="form-label">{{ $t('contact.purpose') }}</label>
                <select
                  id="purpose"
                  v-model="formData.purpose"
                  class="form-input form-select"
                  required
                >
                  <option value="">{{ $t('contact.selectPurpose') }}</option>
                  <option value="cooperation">{{ $t('contact.cooperationOption') }}</option>
                  <option value="purchase">{{ $t('contact.purchaseOption') }}</option>
                  <option value="integration">{{ $t('contact.integrationOption') }}</option>
                  <option value="investment">{{ $t('contact.investmentOption') }}</option>
                  <option value="media">{{ $t('contact.mediaOption') }}</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label for="message" class="form-label">{{ $t('contact.message') }}</label>
              <textarea
                id="message"
                v-model="formData.message"
                class="form-input form-textarea"
                :placeholder="$t('contact.messagePlaceholder')"
                rows="5"
              ></textarea>
            </div>

            <div class="form-footer">
              <button type="submit" class="submit-button" :disabled="isSubmitting">
                <span v-if="!isSubmitting">{{ $t('contact.submit') }}</span>
                <span v-else>{{ $t('contact.submit') }}...</span>
              </button>
              <p class="form-notice">{{ $t('contact.submitNotice') }}</p>
            </div>
          </form>

          <div v-if="submitMessage" :class="['submit-message', submitStatus]">
            {{ submitMessage }}
          </div>
        </div>
      </div>
    </section>

    <FooterSection />
  </div>
</template>

<script>
import Navigation from '@/components/Navigation.vue'
import FooterSection from '@/components/FooterSection.vue'

export default {
  name: 'ContactPage',
  components: {
    Navigation,
    FooterSection
  },
  data() {
    return {
      formData: {
        name: '',
        email: '',
        company: '',
        purpose: '',
        message: ''
      },
      isSubmitting: false,
      submitMessage: '',
      submitStatus: ''
    }
  },
  mounted() {
    if (this.$route.hash === '#contact-form') {
      this.$nextTick(() => {
        setTimeout(() => {
          const el = document.getElementById('contact-form')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      })
    } else {
      window.scrollTo(0, 0)
    }
  },
  methods: {
    async handleSubmit() {
      if (this.isSubmitting) return

      this.isSubmitting = true
      this.submitMessage = ''

      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.formData)
        })

        if (!response.ok) {
          throw new Error('Request failed')
        }

        this.submitStatus = 'success'
        this.submitMessage = this.$t('contact.successMsg')

        // 重置表单
        setTimeout(() => {
          this.formData = {
            name: '',
            email: '',
            company: '',
            purpose: '',
            message: ''
          }
          this.submitMessage = ''
        }, 2000)
      } catch (error) {
        this.submitStatus = 'error'
        this.submitMessage = this.$t('contact.errorMsg')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
#contact-page {
  width: 100%;
  background: #0B1220;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

/* ============= Hero 联系区 ============= */
.contact-hero {
  padding: 120px 0 80px;
  position: relative;
  background: linear-gradient(135deg, rgba(0, 217, 255, 0.05) 0%, rgba(138, 43, 226, 0.05) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.contact-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 50%, rgba(0, 217, 255, 0.02) 0%, transparent 50%),
    radial-gradient(circle at 80% 50%, rgba(138, 43, 226, 0.02) 0%, transparent 50%);
  pointer-events: none;
}

.hero-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;
}

.header-label {
  display: inline-block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;
  max-width: 500px;
}

.hero-card {
  background: #070B14;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 2.5rem;
  position: relative;
  padding-left: 3rem;
}

.hero-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 12px 0 0 12px;
}

.card-header {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.card-location {
  margin-bottom: 1.5rem;
}

.location-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.5rem;
}

.location-address {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
}

.card-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 1.5rem 0;
}

.card-meta {
  display: flex;
  gap: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.65);
}

.icon-marker {
  display: inline-block;
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
}

/* ============= 联系信息区 ============= */
.contact-info {
  padding: 100px 0;
  position: relative;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;
}

.section-subtitle {
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 500px;
  margin: 0 auto;
}

.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
}

.info-card {
  background: #070B14;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 2rem;
  position: relative;
  padding-left: 3rem;
  transition: all 0.3s ease;
}

.info-card:hover {
  border-color: rgba(255, 255, 255, 0.12);
  background: #0F1723;
}

.card-accent-line {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 12px 0 0 12px;
  transition: all 0.3s ease;
}

.info-card:hover .card-accent-line {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%);
  width: 4px;
}

.card-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.2rem;
  flex-shrink: 0;
}

.card-icon svg {
  width: 20px;
  height: 20px;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.8rem;
}

.card-text {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 1rem;
  line-height: 1.6;
}

.card-footer {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

/* ============= 联系表单区 ============= */
#contact-form.contact-form-section {
  scroll-margin-top: 80px; /* 避免被固定导航栏遮挡 */
}
.contact-form-section {
  padding: 100px 0;
  background: rgba(255, 255, 255, 0.02);
  position: relative;
}

.form-wrapper {
  max-width: 700px;
  margin: 0 auto;
  background: #070B14;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 3rem;
  position: relative;
  padding-left: 3.5rem;
}

.form-wrapper::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 12px 0 0 12px;
}

.form-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.form-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.8rem;
}

.form-subtitle {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.form-group:has(.form-textarea) {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.9rem 1rem;
  color: white;
  font-size: 0.95rem;
  font-family: inherit;
  transition: all 0.3s ease;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.form-input:focus {
  outline: none;
  border-color: rgba(0, 185, 255, 0.5);
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 2px rgba(0, 185, 255, 0.1);
}

.form-select:focus {
  outline: none;
  border-color: rgba(0, 185, 255, 0.5);
  background-color: rgba(255, 255, 255, 0.06);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='rgba(0,185,255,0.9)' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.8rem center;
  box-shadow: 0 0 0 2px rgba(0, 185, 255, 0.1);
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='rgba(255,255,255,0.7)' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.8rem center;
  padding-right: 2.5rem;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.03);
  color: white;
}

/* 针对 option 元素的样式 */
.form-select option {
  background-color: #0B1220;
  color: #F9FAFB;
  padding: 0.5rem;
  margin: 0.2rem 0;
}

.form-select option:checked {
  background: linear-gradient(rgba(0, 185, 255, 0.2), rgba(0, 185, 255, 0.2));
  background-color: rgba(0, 185, 255, 0.2);
  color: #00B9FF;
}

.form-select optgroup {
  background-color: #0B1220;
  color: #F9FAFB;
}

.form-textarea {
  resize: none;
  font-family: inherit;
}

.form-footer {
  margin-top: 1rem;
  text-align: center;
}

.submit-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.95rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  display: inline-block;
}

.submit-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.25);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-notice {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 1rem;
  font-style: italic;
}

.submit-message {
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 6px;
  text-align: center;
  font-size: 0.9rem;
  animation: slideDown 0.3s ease;
}

.submit-message.success {
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: rgba(74, 222, 128, 0.9);
}

.submit-message.error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: rgba(239, 68, 68, 0.9);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============= 响应式 ============= */
@media (max-width: 1024px) {
  .hero-wrapper {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .info-cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-wrapper {
    padding: 2rem;
    padding-left: 2.5rem;
  }
}

@media (max-width: 640px) {
  .contact-hero {
    padding: 60px 0 50px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .hero-card {
    padding: 1.5rem;
    padding-left: 2.5rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .info-cards-grid {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }

  .info-card {
    padding: 1.5rem;
    padding-left: 2.5rem;
  }

  .contact-form-section {
    padding: 60px 0;
  }

  .form-wrapper {
    padding: 1.5rem;
    padding-left: 2.5rem;
  }

  .form-title {
    font-size: 1.3rem;
  }

  .form-label {
    font-size: 0.8rem;
  }

  .form-input {
    padding: 0.8rem 0.85rem;
    font-size: 0.9rem;
  }
}
</style>
